import { AlertTriangle, Loader2, RefreshCcw, Sparkles, WandSparkles } from 'lucide-react';
import type { AiAnnotation } from '@/types/ai';
import { Button } from '@/components/ui/button';

interface AiSummaryCardProps {
  annotation: AiAnnotation | null;
  quickSummary: string | null;
  isLoading: boolean;
  error: string | null;
  isSupported: boolean;
  onGenerate: (options?: { force?: boolean }) => Promise<AiAnnotation | null>;
  onRetry: () => Promise<AiAnnotation | null>;
}


function resolveSummary(annotation: AiAnnotation | null) {
  if (!annotation) {
    return '';
  }
  if (annotation.summary) {
    return annotation.summary;
  }
  if (annotation.result && typeof annotation.result === 'object') {
    const resultSummary = (annotation.result as { summary?: unknown }).summary;
    if (typeof resultSummary === 'string') {
      return resultSummary;
    }
  }
  return '';
}

function resolveList(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
    .filter((entry) => entry.length > 0)
    .slice(0, 5);
}

export function AiSummaryCard({
  annotation,
  quickSummary,
  isLoading,
  error,
  isSupported,
  onGenerate,
  onRetry,
}: AiSummaryCardProps) {
  if (!isSupported) {
    return null;
  }

  const summaryText = resolveSummary(annotation);
  const resultData = (annotation?.result ?? null) as Record<string, unknown> | null;
  const highlights = resolveList(resultData?.['highlights']);
  const actionItems = resolveList(resultData?.['actionItems'] ?? resultData?.['action_items']);
  const status = annotation?.status ?? 'idle';
  const hasCompleteSummary = status === 'complete' && Boolean(summaryText);
  const hasQuickSummary = Boolean(quickSummary) && !hasCompleteSummary;

  return (
    <div className="rounded-2xl border border-primary/20 bg-primary/5 p-4 shadow-sm">
        <div className="space-y-4 flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-primary">
            <Sparkles className="h-4 w-4" />
            AI Summary
          </div>
          <div className="flex items-center gap-2">
            {hasQuickSummary && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1 text-xs"
                disabled={isLoading}
                onClick={() => {
                  void onGenerate();
                }}
              >
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <WandSparkles className="h-4 w-4" />}
                Get full summary
              </Button>
            )}
            {hasCompleteSummary && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1 text-xs"
                disabled={isLoading}
                onClick={() => {
                  void onGenerate({ force: true });
                }}
              >
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" />}
                Refresh
              </Button>
            )}
          </div>
        </div>

        {hasCompleteSummary && (
          <div className="space-y-4 text-sm">
            <p className="text-foreground">{summaryText}</p>
            {highlights.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Highlights</p>
                <ul className="list-disc space-y-1 pl-4 text-foreground">
                  {highlights.map((item, index) => (
                    <li key={`${item}-${index}`}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
            {actionItems.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Action Items</p>
                <ul className="list-disc space-y-1 pl-4 text-foreground">
                  {actionItems.map((item, index) => (
                    <li key={`${item}-${index}`}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {hasQuickSummary && !isLoading && status !== 'error' && (
          <div className="text-sm">
            <p className="text-foreground">{quickSummary}</p>
          </div>
        )}

        {!hasCompleteSummary && !hasQuickSummary && !isLoading && status !== 'error' && (
          <div className="flex flex-col gap-3 text-sm">
            <Button
              type="button"
              variant="default"
              className="gap-2"
              disabled={isLoading}
              onClick={() => {
                void onGenerate();
              }}
            >
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Summarize message
            </Button>
          </div>
        )}

        {status === 'error' && (
          <div className="flex items-start gap-3 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            <div className="space-y-2">
              <p>{annotation?.error || error || 'Unable to summarize this message.'}</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-destructive text-destructive"
                disabled={isLoading}
                onClick={() => {
                  void onRetry();
                }}
              >
                {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Try again
              </Button>
            </div>
          </div>
        )}

        {isLoading && status !== 'complete' && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Generating summary…
          </div>
        )}
        {error && status !== 'error' && (
          <p className="text-xs text-destructive">{error}</p>
        )}
    </div>
  );
}
